import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/utils-cart";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Image as ImageIcon } from "lucide-react";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const empty = { title: "", description: "", category: "fashion", price: 0, stock: 0, images: [], tags: [], is_published: true };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/products/mine").then((r) => setProducts(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock), images: form.images.filter(Boolean), tags: form.tags };
      if (editing) await api.patch(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved");
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ ...p, tags: p.tags || [], images: p.images || [] });
    setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <DashboardLayout title="Products" subtitle="Manage your catalog. Products go live immediately."
      actions={<button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="v-btn-primary" data-testid="products-add"><Plus size={14} /> Add product</button>}
    >
      {open && (
        <div className="v-card mb-6">
          <h2 className="font-display text-lg font-semibold text-primary">{editing ? "Edit product" : "New product"}</h2>
          <form onSubmit={save} className="mt-4 grid gap-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="v-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="product-title" />
              <select className="v-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="product-category">
                {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <textarea rows={3} className="v-input" placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required data-testid="product-description" />
            <div className="grid sm:grid-cols-3 gap-3">
              <input type="number" step="0.01" className="v-input" placeholder="Price CAD" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} required data-testid="product-price" />
              <input type="number" className="v-input" placeholder="Stock" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} required data-testid="product-stock" />
              <input className="v-input" placeholder="Tags (comma)" value={form.tags?.join(",")} onChange={(e) => setForm({...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} data-testid="product-tags" />
            </div>
            <input className="v-input" placeholder="Image URL (https://…)" value={form.images?.[0] || ""} onChange={(e) => setForm({...form, images: [e.target.value]})} data-testid="product-image" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} data-testid="product-published" /> Published</label>
            <div className="flex gap-2">
              <button className="v-btn-primary" data-testid="product-save">{editing ? "Update" : "Create"}</button>
              <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="v-btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="v-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3">Product</th>
              <th className="p-3">Category</th>
              <th className="p-3 text-right">Price</th>
              <th className="p-3 text-right">Stock</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="p-3 flex items-center gap-3">
                  {p.images?.[0] ? <img src={p.images[0]} className="w-10 h-10 object-cover rounded-sm" /> : <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center"><ImageIcon size={14} className="text-muted-foreground" /></div>}
                  <div>
                    <div className="font-medium text-primary">{p.title}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.id.slice(0, 8)}</div>
                  </div>
                </td>
                <td className="p-3 capitalize">{p.category}</td>
                <td className="p-3 text-right font-mono">{formatCAD(p.price)}</td>
                <td className="p-3 text-right font-mono">{p.stock}</td>
                <td className="p-3 text-right">
                  <button onClick={() => edit(p)} className="v-btn-ghost" data-testid={`product-edit-${p.id}`}><Edit2 size={14} /></button>
                  <button onClick={() => del(p.id)} className="v-btn-ghost text-destructive" data-testid={`product-delete-${p.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No products yet. Click "Add product" to get started.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
